import { Button, Stack, Text } from '@mantine/core';
import { FormProvider, UseFormReturn } from 'react-hook-form';
import { Textarea, TextInput } from 'react-hook-form-mantine';
import { InlineLoader } from '@dealstack/frontend/shared/components/loaders/InlineLoader/InlineLoader';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { AdvancedSwitch } from '@dealstack/frontend/shared/components/Switch/AdvancedSwitch.tsx';
import {
	CREATE_CUSTOM_FIELD_FORM_FIELD,
	CreateCustomFieldDto,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/InvestorData/components/investorData.zod.ts';

interface Props {
	form: UseFormReturn<CreateCustomFieldDto>;
	isEditMode: boolean;
	isLoading: boolean;
	onSubmit: (values: CreateCustomFieldDto) => void | Promise<void>;
	onDelete?: () => void;
}

export const CustomFieldFormBody = ({ form, isEditMode, isLoading, onSubmit, onDelete }: Props) => {
	const tMipManage = useTranslationMipManage();

	return (
		<>
			{isLoading && <InlineLoader />}
			<FormProvider {...form}>
				<form onSubmit={form.handleSubmit(onSubmit)}>
					<TextInput
						name={CREATE_CUSTOM_FIELD_FORM_FIELD.name}
						control={form.control}
						label={tMipManage('investor_data.custom_fields.field_name')}
						size="md"
						placeholder={tMipManage(
							'investor_data.custom_fields.field_name_placeholder'
						)}
					/>

					<Textarea
						name={CREATE_CUSTOM_FIELD_FORM_FIELD.instruction}
						control={form.control}
						label={tMipManage('investor_data.custom_fields.description')}
						placeholder={tMipManage(
							'investor_data.custom_fields.description_placeholder'
						)}
						size="md"
						autosize
						minRows={4}
					/>

					<Stack gap={8}>
						<Text size="sm" fw={600}>
							{tMipManage('investor_data.custom_fields.status')}
						</Text>
						<AdvancedSwitch
							selectedValue={form.watch(CREATE_CUSTOM_FIELD_FORM_FIELD.isChecked)}
							onChange={(e) =>
								form.setValue(CREATE_CUSTOM_FIELD_FORM_FIELD.isChecked, !!e)
							}
							rightText={tMipManage('investor_data.custom_fields.required')}
							disabled={isLoading}
							selectedColor="dark.8"
							unselectedColor="dark.8"
							selectedFontWeight={400}
							unselectedLetterSpacing="0"
						/>
					</Stack>

					<Button fullWidth mt="auto" type="submit" disabled={isLoading}>
						{isEditMode
							? tMipManage('investor_data.custom_fields.update_field')
							: tMipManage('investor_data.custom_fields.create_field')}
					</Button>

					{isEditMode && onDelete && (
						<Button
							fullWidth
							variant="outline"
							color="red"
							mt="xs"
							onClick={onDelete}
							disabled={isLoading}
						>
							{tMipManage('investor_data.custom_fields.delete_field')}
						</Button>
					)}
				</form>
			</FormProvider>
		</>
	);
};
